/**
 * The ONE fetch wrapper (P2 §3.5). Relative base `/api` (same origin; nginx strips the prefix → rnr_us_api), so no
 * host is ever compiled into the bundle. Cookie auth (`__Host-rnr_s`), CSRF header on unsafe methods (P1 §3.5),
 * `Idempotency-Key` on every POST, `If-Match` on versioned PATCH, `{ok,error}` envelope unwrapped, request id kept
 * for support toasts. A 401 that ends the session sends the user to /login?next=… with the return path preserved; a 401
 * that answers what was typed (a wrong password or code) is thrown with its own code and the session stays. A 403
 * `mfa_required` (the session owes the second sign-in step) is handed to the second-step handler.
 */
import { sessionEnded } from './secondStep';

export const API_BASE = '/api';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public requestId: string | null, public body: unknown = null) {
    super(code);
  }
  /** "Ref: 7f3a…" for toasts. */
  get ref() { return this.requestId ? `Ref: ${this.requestId.slice(0, 8)}` : ''; }
}

/** For TanStack `retry`: one more try after a network or server error, none after a 4xx (a refusal, or 429: too many
 * requests already, where an instant retry only adds one). */
export const retryOnce = (n: number, e: unknown) => n < 1 && !(e instanceof ApiError && e.status < 500);

export interface ReqOpts {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  /** Optimistic concurrency: sent as If-Match on PATCH/POST. */
  version?: number;
  /** Stable key for retries of the same intent (defaults to a fresh uuid per call). */
  idempotencyKey?: string;
  signal?: AbortSignal;
  raw?: boolean;
}

let csrfToken: string | null = null;
export const setCsrf = (t: string | null) => { csrfToken = t; };
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn; };
let onSecondStep: (() => void) | null = null;
export const setSecondStepHandler = (fn: (() => void) | null) => { onSecondStep = fn; };
let lastRequestId: string | null = null;
export const getLastRequestId = () => lastRequestId;

export const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

function qs(q?: ReqOpts['query']) {
  if (!q) return '';
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export async function request<T = unknown>(method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, o: ReqOpts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const unsafe = method !== 'GET';
  if (unsafe) {
    headers['X-RNR-CSRF'] = csrfToken ?? '1';
    if (method === 'POST') headers['Idempotency-Key'] = o.idempotencyKey ?? newKey();
  }
  if (o.version != null) headers['If-Match'] = String(o.version);
  let body: BodyInit | undefined;
  if (o.body instanceof FormData || o.body instanceof Blob) body = o.body;
  else if (o.body !== undefined) { headers['Content-Type'] = 'application/json'; body = JSON.stringify(o.body); }

  const res = await fetch(`${API_BASE}${path}${qs(o.query)}`, { method, headers, body, credentials: 'same-origin', signal: o.signal });
  const rid = res.headers.get('x-request-id');
  if (rid) lastRequestId = rid;
  if (o.raw) return res as unknown as T;

  let json: any = null;
  const text = await res.text();
  if (text) { try { json = JSON.parse(text); } catch { json = null; } }

  if (res.status === 401) {
    const code = typeof json?.error === 'string' ? json.error : 'unauthorized';
    if (sessionEnded(401, code)) onUnauthorized?.();
    throw new ApiError(401, code, rid, json);
  }
  if (res.status === 403 && json?.error === 'mfa_required') onSecondStep?.();
  if (!res.ok || (json && json.ok === false)) {
    throw new ApiError(res.status, json?.error ?? `http_${res.status}`, rid, json);
  }
  // Envelope: {ok:true, ...payload} or {ok:true, data}. Unwrap `data` when it is the only payload key.
  if (json && typeof json === 'object' && 'ok' in json) {
    const { ok: _ok, ...rest } = json;
    const keys = Object.keys(rest);
    return (keys.length === 1 && keys[0] === 'data' ? rest.data : rest) as T;
  }
  return json as T;
}

export const http = {
  get: <T>(p: string, o?: ReqOpts) => request<T>('GET', p, o),
  post: <T>(p: string, body?: unknown, o?: ReqOpts) => request<T>('POST', p, { ...o, body }),
  patch: <T>(p: string, body?: unknown, o?: ReqOpts) => request<T>('PATCH', p, { ...o, body }),
  put: <T>(p: string, body?: unknown, o?: ReqOpts) => request<T>('PUT', p, { ...o, body }),
  del: <T>(p: string, o?: ReqOpts) => request<T>('DELETE', p, o),
};
export type Http = typeof http;

/** Staff product API prefix (P1 §3.6: `/v1/app/*`). */
export const APP = '/v1/app';
